
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";

interface Comment {
  id: string;
  content: string;
  created_at: string;
  created_by: string;
  created_by_uuid: string | null;
  updated_at?: string;
  migration?: boolean;
  profiles?: {
    first_name: string | null;
    last_name: string | null;
  };
}

export const useCompanyComments = (companyId: string | undefined) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  
  const fetchComments = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('company_comments')
        .select(`
          *,
          profiles:created_by_uuid (
            first_name,
            last_name
          )
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      if (data) {
        setComments(data as Comment[]);
      }
    } catch (error) {
      console.error("Error fetching company comments:", error);
      // Use mock data when in development or if there's an error
      setComments([
        {
          id: "1",
          content: "First meeting went very well. They're interested in our Java developers.",
          created_at: "2023-09-15T14:30:00Z",
          created_by: "Kevin Thibaut",
          created_by_uuid: null
        },
        {
          id: "2",
          content: "Follow-up scheduled for next month. They're expanding their team and will need 3-5 developers.",
          created_at: "2023-09-20T10:15:00Z",
          created_by: "Sarah Johnson",
          created_by_uuid: null
        }
      ]);
    } finally {
      setLoading(false);
    }
  };
  
  const logCompanyHistory = async (action: string, description: string) => {
    if (!companyId) return;
    
    try {
      // Get the current user for the performed_by field
      let userName = "Current User";
      const { data: userSession } = await supabase.auth.getSession();
      if (userSession.session?.user) {
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('id', userSession.session.user.id)
          .single();
        
        if (userProfile) {
          userName = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim() || "Current User";
        }
      }
      
      // NOTE: History tracking is now handled automatically by database triggers
      // No manual history logging needed here
        
    } catch (error) {
      console.error("Error logging company history:", error);
      // We don't want to block the main operation if logging fails
    }
  };
  
  const addComment = async (content: string) => {
    if (!companyId || !content.trim() || !user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('company_comments')
        .insert({
          company_id: companyId,
          content,
          created_by: "Current User", // Keep for backward compatibility
          created_by_uuid: user.id, // New UUID field
        })
        .select(`
          *,
          profiles:created_by_uuid (
            first_name,
            last_name
          )
        `);
      
      if (error) throw error;
      
      // Log comment action to history
      await logCompanyHistory(
        "Comment Added", 
        `Comment added by ${user.id}`
      );
      
      toast({
        title: "Comment added",
        description: "Your comment has been added successfully",
      });
      
      if (data) {
        setComments(prev => [data[0] as Comment, ...prev]);
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast({
        title: "Error adding comment",
        description: "Could not add your comment",
        variant: "destructive",
      });
      
      // For demo/development, add the comment locally
      const mockNewComment = {
        id: `mock-${Date.now()}`,
        content,
        created_at: new Date().toISOString(),
        created_by: "Current User",
        created_by_uuid: user?.id || null
      };
      
      setComments([mockNewComment, ...comments]);
    }
  };
  
  useEffect(() => {
    fetchComments();
  }, [companyId]);
  
  const deleteComment = async (commentId: string) => {
    try {
      const { error } = await supabase
        .from('company_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      // Log comment deletion to history
      await logCompanyHistory(
        "Comment Deleted", 
        `Comment deleted by ${user?.id || "Unknown"}`
      );

      toast({
        title: "Comment deleted",
        description: "The comment has been deleted successfully",
      });

      // Update local state
      setComments(prev => prev.filter(comment => comment.id !== commentId));
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast({
        title: "Error deleting comment",
        description: "Could not delete the comment",
        variant: "destructive",
      });
    }
  };

  const updateComment = async (commentId: string, content: string) => {
    if (!content.trim()) return;

    try {
      const { error } = await supabase
        .from('company_comments')
        .update({ 
          content,
          updated_at: new Date().toISOString()
        })
        .eq('id', commentId);

      if (error) throw error;

      // Log comment update to history
      await logCompanyHistory(
        "Comment Updated", 
        `Comment updated by ${user?.id || "Unknown"}`
      );

      toast({
        title: "Comment updated",
        description: "The comment has been updated successfully",
      });

      // Refresh comments to get the updated data
      await fetchComments();
    } catch (error) {
      console.error("Error updating comment:", error);
      toast({
        title: "Error updating comment",
        description: "Could not update the comment",
        variant: "destructive",
      });
    }
  };

  return {
    comments,
    loading,
    addComment,
    deleteComment,
    updateComment,
    refreshComments: fetchComments
  };
};
